import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../../../components/atoms/forms/Button';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import {
  formatBattery,
  formatDeltaLabel,
  formatLastPingSeconds,
  formatDriverNames,
} from '../../../utils/displayFormat';
import {
  LIFECYCLE_LABEL,
  DELTA_TIER_STYLE,
  toneClassVariants,
} from './statusStyles';
import { driverRecordShape, timelineRowShape } from './driverRecordShape';

// Timeline dot glyphs per stop state (pending rows show no glyph).
const DOT_CHARS = { done: '✓', current: '●' };

// Inline icons sized for the 12px footer meta row. `currentColor` lets each
// icon inherit the text color of its wrapping <span> (e.g. battery turns red
// when wrapped in `.battery-low`).
const IconSignal = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 10 10"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="1" y="6.5" width="1.5" height="2" rx="0.3" fill="currentColor" />
    <rect x="3.5" y="4.5" width="1.5" height="4" rx="0.3" fill="currentColor" />
    <rect x="6" y="2.5" width="1.5" height="6" rx="0.3" fill="currentColor" />
    <rect x="8.5" y="0.5" width="1.5" height="8" rx="0.3" fill="currentColor" />
  </svg>
);

const IconBattery = () => (
  <svg
    width="14"
    height="11"
    viewBox="0 0 14 10"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <rect
      x="0.75"
      y="2.25"
      width="10.5"
      height="5.5"
      rx="1"
      stroke="currentColor"
      strokeWidth="1"
      fill="none"
    />
    <rect
      x="11.75"
      y="3.75"
      width="1.5"
      height="2.5"
      rx="0.4"
      fill="currentColor"
    />
    <rect x="2" y="3.5" width="6.5" height="3" rx="0.4" fill="currentColor" />
  </svg>
);

// Card class prefix is `dc-` to keep the parent list's `& .className` selectors
// from cascading into nested elements (see CLAUDE.md note on class-name
// collisions when a child renders inside a parent's StyledDiv).
const StyledCard = styledComponent('div', {
  position: 'relative',
  padding: '20px 24px',
  borderBottom: `1px solid ${theme.colors.borderLight}`,
  background: theme.colors.bgCard,
  transition: theme.transitions.default,

  '&:hover': {
    background: theme.colors.bgMuted,
  },
  '&:last-of-type': {
    borderBottom: 'none',
  },

  '& .dc-accent': {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '3px',
    background: theme.colors.statusNeutral,
  },

  '& .dc-top': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing.fieldGap,
  },
  '& .dc-identity': {
    minWidth: 0,
  },
  '& .dc-name': {
    fontSize: theme.sizes.subheadFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
    lineHeight: 1.3,
  },
  '& .dc-meta-row': {
    marginTop: '3px',
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px',
  },
  '& .dc-view-route': {
    marginTop: '4px',
  },
  '& .dc-meta-sep': {
    color: theme.colors.textHint,
  },
  '& .dc-status-dot': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    fontWeight: theme.fontWeights.medium,
  },
  '& .dc-status-dot::before': {
    content: '""',
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: theme.radii.circle,
    background: 'currentColor',
  },

  '& .dc-eta': {
    flex: '0 0 auto',
    textAlign: 'right',
    minWidth: '64px',
  },
  '& .dc-eta-value': {
    fontSize: theme.sizes.statFontSize,
    fontWeight: theme.fontWeights.semibold,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },
  '& .dc-eta-unit': {
    display: 'block',
    marginTop: '2px',
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  '& .dc-timeline': {
    marginTop: theme.spacing.fieldGap,
    position: 'relative',
  },
  '& .dc-tl-row': {
    display: 'grid',
    gridTemplateColumns: '20px 1fr auto',
    columnGap: '10px',
    alignItems: 'flex-start',
    padding: '6px 0',
    position: 'relative',
  },
  '& .dc-tl-row + .dc-tl-row::before': {
    content: '""',
    position: 'absolute',
    left: '9px',
    top: '-6px',
    height: '12px',
    width: '2px',
    background: theme.colors.borderLight,
  },
  '& .dc-tl-dot': {
    width: '20px',
    height: '20px',
    borderRadius: theme.radii.circle,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.sizes.microFontSize,
    fontWeight: theme.fontWeights.semibold,
    lineHeight: 1,
  },
  '& .dc-tl-dot.done': {
    background: theme.colors.statusOnTrackBg,
    color: theme.colors.statusOnTrack,
  },
  '& .dc-tl-dot.current': {
    background: theme.colors.statusBehindBg,
    color: theme.colors.statusBehind,
  },
  '& .dc-tl-dot.pending': {
    background: theme.colors.bgMuted,
    color: theme.colors.textMuted,
    border: `1px solid ${theme.colors.borderLight}`,
  },
  '& .dc-tl-text': {
    fontSize: theme.sizes.smallFontSize,
    minWidth: 0,
    lineHeight: 1.35,
  },
  '& .dc-tl-stop-name': {
    color: theme.colors.textDark,
    fontWeight: theme.fontWeights.medium,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .dc-tl-stop-code': {
    fontSize: theme.sizes.microFontSize,
    fontWeight: theme.fontWeights.normal,
    color: theme.colors.textHint,
    marginLeft: '6px',
    fontFamily: theme.fonts.mono,
  },
  '& .dc-tl-stop-meta': {
    color: theme.colors.textMuted,
    fontSize: theme.sizes.captionFontSize,
  },
  '& .dc-tl-delta': {
    padding: '2px 8px',
    borderRadius: theme.radii.badge,
    fontSize: theme.sizes.captionFontSize,
    fontWeight: theme.fontWeights.medium,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  ...toneClassVariants('dc-tl-delta', DELTA_TIER_STYLE),

  '& .dc-tl-more': {
    paddingTop: '6px',
  },

  '& .dc-footer': {
    marginTop: theme.spacing.fieldGap,
    paddingTop: theme.spacing.fieldGap,
    borderTop: `1px solid ${theme.colors.borderLight}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing.fieldGap,
    flexWrap: 'wrap',
  },
  '& .dc-footer-no-border': {
    borderTop: 'none',
    marginTop: '10px',
    paddingTop: 0,
  },
  '& .dc-footer-meta': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '12px',
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textMuted,
  },
  '& .dc-footer-meta .dc-meta-item': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  '& .dc-footer-meta .battery-low': {
    color: theme.colors.statusOffTrack,
    fontWeight: theme.fontWeights.semibold,
  },
});

function ActiveMeta({ driver }) {
  const batteryLabel = formatBattery(driver.batteryLevel);
  const pingLabel = formatLastPingSeconds(driver.lastPingSecondsAgo);
  const isBatteryLow =
    driver.batteryLevel !== undefined &&
    driver.batteryLevel !== null &&
    driver.batteryLevel <= 0.2;
  return (
    <div className="dc-footer-meta">
      <span className="dc-meta-item">
        <strong>{`${driver.stopsCompleted} / ${driver.stopsTotal}`}</strong>
        {' stops'}
      </span>
      {/* Signed: overnight routes run under Google's baseline, so a negative
          total is normal and still worth showing. Hidden only when no leg
          has matched yet. */}
      {driver.trafficDelayTodayMin !== null &&
        driver.trafficDelayTodayMin !== undefined && (
          <span className="dc-meta-item">
            {`Traffic ${formatDeltaLabel(driver.trafficDelayTodayMin, {
              zeroLabel: null,
            })}`}
          </span>
        )}
      {pingLabel && (
        <span className="dc-meta-item">
          <IconSignal />
          {pingLabel}
        </span>
      )}
      {batteryLabel && (
        <span className={`dc-meta-item ${isBatteryLow ? 'battery-low' : ''}`}>
          <IconBattery />
          {batteryLabel}
        </span>
      )}
    </div>
  );
}

ActiveMeta.propTypes = {
  driver: PropTypes.shape({
    stopsCompleted: PropTypes.number,
    stopsTotal: PropTypes.number,
    lastPingSecondsAgo: PropTypes.number,
    batteryLevel: PropTypes.number,
    trafficDelayTodayMin: PropTypes.number,
  }).isRequired,
};

function Timeline({
  rows,
  fullRows,
  remainingStopsCount,
  isExpanded,
  onToggleExpand,
  onSelectStop,
}) {
  if (!rows || rows.length === 0) return null;
  const visibleRows =
    isExpanded && fullRows && fullRows.length > 0 ? fullRows : rows;
  return (
    <div className="dc-timeline">
      {visibleRows.map((row) => {
        const deltaTier = DELTA_TIER_STYLE[row.deltaTier]
          ? row.deltaTier
          : 'eta';
        const dotChar = DOT_CHARS[row.state] || '';
        return (
          <div key={`${row.state}-${row.stopCode}`} className="dc-tl-row">
            <span className={`dc-tl-dot ${row.state}`}>{dotChar}</span>
            <div className="dc-tl-text">
              <div className="dc-tl-stop-name">
                {onSelectStop && row.stopCode ? (
                  <Button
                    variant="link-primary"
                    title="Show this stop on the route map"
                    onClick={() => onSelectStop(row.stopCode)}
                  >
                    {row.stopName}
                  </Button>
                ) : (
                  row.stopName
                )}
                <span className="dc-tl-stop-code">{row.stopCode}</span>
              </div>
              <div className="dc-tl-stop-meta">
                {`${row.positionLabel} · ${row.timeLabel} · ${row.durationLabel}`}
              </div>
            </div>
            <span className={`dc-tl-delta ${deltaTier}`}>{row.deltaLabel}</span>
          </div>
        );
      })}
      {(remainingStopsCount > 0 || isExpanded) && onToggleExpand && (
        <div className="dc-tl-more">
          <Button variant="link-primary" onClick={onToggleExpand}>
            {isExpanded
              ? 'Show less'
              : `+ ${remainingStopsCount} more ${
                  remainingStopsCount === 1 ? 'stop' : 'stops'
                }`}
          </Button>
        </div>
      )}
    </div>
  );
}

Timeline.propTypes = {
  rows: PropTypes.arrayOf(timelineRowShape),
  fullRows: PropTypes.arrayOf(timelineRowShape),
  remainingStopsCount: PropTypes.number,
  isExpanded: PropTypes.bool,
  onToggleExpand: PropTypes.func,
  // Receives the clicked row's stop id (`stopCode`).
  onSelectStop: PropTypes.func,
};

Timeline.defaultProps = {
  rows: [],
  fullRows: [],
  remainingStopsCount: 0,
  isExpanded: false,
  onToggleExpand: null,
  onSelectStop: null,
};

function DriverCard({ driver, onViewBreakdown, onSelectRoute, onSelectStop }) {
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(false);
  const isActive =
    driver.status === 'on-track' ||
    driver.status === 'behind' ||
    driver.status === 'off-track';
  const isNotStarted = driver.status === 'not-started';
  const hasDrivers = (driver.driverNames || []).length > 0;

  // The only footer action is "View breakdown" for a reported breakdown.
  let actionButton = null;
  if (driver.breakdownReported) {
    actionButton = (
      <Button
        variant="danger-outline"
        onClick={onViewBreakdown ? () => onViewBreakdown(driver) : null}
      >
        View breakdown
      </Button>
    );
  }

  return (
    <StyledCard data-testid={`driver-card-${driver.driverId}`}>
      <span className="dc-accent" />

      <div className="dc-top">
        <div className="dc-identity">
          <div className="dc-name">
            {formatDriverNames(driver.driverNames, driver.routeName)}
          </div>
          <div className="dc-meta-row">
            {hasDrivers && <span>{driver.routeName}</span>}
            {hasDrivers && <span className="dc-meta-sep">·</span>}
            {driver.vehicleName && (
              <>
                <span>{driver.vehicleName}</span>
                <span className="dc-meta-sep">·</span>
              </>
            )}
            <span className="dc-status-dot">
              {LIFECYCLE_LABEL[driver.status]}
            </span>
            {!hasDrivers && isNotStarted && (
              <>
                <span className="dc-meta-sep">·</span>
                <span>Awaiting dispatch</span>
              </>
            )}
          </div>
          {onSelectRoute && (
            <div className="dc-view-route">
              <Button
                variant="link-primary"
                onClick={() => onSelectRoute(driver.routeId)}
                title="Open this route on the map"
              >
                View route
              </Button>
            </div>
          )}
        </div>
        {isNotStarted && (
          <div className="dc-eta">
            <span className="dc-eta-value">
              {driver.plannedDepartureLabel || '—'}
            </span>
            <span className="dc-eta-unit">depart</span>
          </div>
        )}
      </div>

      <Timeline
        rows={driver.timeline}
        fullRows={driver.fullTimeline}
        remainingStopsCount={driver.remainingStopsCount}
        isExpanded={isTimelineExpanded}
        onToggleExpand={() => setIsTimelineExpanded((v) => !v)}
        onSelectStop={
          onSelectStop ? (stopId) => onSelectStop(driver.routeId, stopId) : null
        }
      />

      <div className="dc-footer">
        {isActive ? (
          <ActiveMeta driver={driver} />
        ) : (
          <div className="dc-footer-meta">
            <span>
              <strong>{`${driver.stopsCompleted} / ${driver.stopsTotal}`}</strong>
              {' stops'}
            </span>
            {isNotStarted && <span>Awaiting depot</span>}
            {driver.status === 'completed' && driver.backAtDepotLabel && (
              <span>{`Back at depot ${driver.backAtDepotLabel}`}</span>
            )}
          </div>
        )}

        {actionButton}
      </div>
    </StyledCard>
  );
}

DriverCard.propTypes = {
  driver: driverRecordShape.isRequired,
  onViewBreakdown: PropTypes.func,
  onSelectRoute: PropTypes.func,
  // (routeId, stopId) — open the route's map focused on that stop.
  onSelectStop: PropTypes.func,
};

DriverCard.defaultProps = {
  onViewBreakdown: null,
  onSelectRoute: null,
  onSelectStop: null,
};

export default DriverCard;
